import { useState } from "react";
import { ArrowRight, Key } from "@phosphor-icons/react";
import { TextField } from "../../design-system";
import { FieldLabel } from "./FieldLabel";
import { WizardCta } from "./WizardCta";
import { useSnippetContext } from "../docs/snippetContext";
import { useIdentityClient } from "../../identity";
import {
  MANUAL_AGENT_KIND,
  kindSpec,
  useAgentClient,
  useAgents,
  type Agent,
  type AgentKind,
} from "../../agents";
import { useWorkspace } from "../../workspaces";
import { workspaceKeyPrefix } from "../../identity/active";
import { friendlyError, invalidateQueries, useFilters } from "../../analytics";
import {
  trackApiKeyCreated,
  trackButtonPressed,
} from "../../analytics/tracking";
import { logger } from "../../services/telemetry";
import { WizardFrame } from "./WizardFrame";
import { AgentTypePicker } from "./AgentTypePicker";
import { PROVIDER_KEY_COPY, PROVIDER_KEY_HEADLINE } from "./providerKeyCopy";
import { cliRouteOffered } from "./cliHandoff";

/** The link out to the hand-configuration guide — also its analytics label. */
const MANUAL_SETUP_LINK = "Manually configure your settings here";

/** Derive an agent_id from a free-text name: lower-cased, trimmed, kebab-cased. */
export function toAgentId(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The name step 1 opens with: the agent type slugged, plus the first number free in
 *  the workspace ("claude-code-1", "langchain-8"). Naming is a decision the reader has
 *  no basis to make yet, and the backend 409s on a taken id.
 *
 *  Already kebab-case, so it IS the agent_id `toAgentId` derives from it — which is
 *  what makes the counter enough to keep the prefill off that 409. */
export function suggestAgentName(
  kind: AgentKind | null,
  taken: readonly Pick<Agent, "agent_id">[],
): string {
  /* "my-agent" covers both kinds of no-answer: before the reader picks a type, and the
   * hand-configured kind, which names the absence of a known one. An agent called
   * "other-1" says nothing, and neither would "unset-1". */
  const slug =
    kind === null || kind === "other"
      ? "my-agent"
      : toAgentId(kindSpec(kind).label);
  const used = new Set(taken.map((a) => a.agent_id));

  let n = 1;
  while (used.has(`${slug}-${n}`)) n += 1;
  return `${slug}-${n}`;
}

export interface SetupResult {
  agentId: string;
  kind: AgentKind;
  /** Display name, editable on the way back — unlike agentId, which is the routing key. */
  name: string;
}

/* Step 1 — name the agent and pick its type. Continue registers the agent and, unless the
 * CLI will configure it, mints the key; in edit mode (`existing` set) the agent already
 * exists, so it only updates the kind. */
export function SetupAgentStep({
  existing,
  onContinue,
  ctaLabel = "Continue",
}: {
  existing: SetupResult | null;
  onContinue: (result: SetupResult) => void;
  /** Label for the create-mode CTA. Edit mode is always "Save and continue". */
  ctaLabel?: string;
}) {
  const { workspace } = useWorkspace();
  const identity = useIdentityClient();
  const agents = useAgentClient();
  const { bumpRefresh } = useFilters();
  const { data: existingAgents } = useAgents();
  const { selectNewAgent, setAi21Key } = useSnippetContext();

  /* `null` until the reader edits the field. While it holds, the name is derived, so
   * the prefill follows the agent type and the workspace list once that loads; the
   * first keystroke pins their value and nothing overwrites it after that. */
  const [typedName, setTypedName] = useState<string | null>(
    existing?.name ?? null,
  );
  /* Null until the reader picks a tile: creation pre-selects nothing, so every agent
   * carries a type its owner actually chose. Edit mode opens on the stored one. */
  const [kind, setKind] = useState<AgentKind | null>(existing?.kind ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const name = typedName ?? suggestAgentName(kind, existingAgents ?? []);
  const editing = existing !== null;
  const agentId = editing ? existing.agentId : toAgentId(name);
  const named = agentId.length > 0 && name.trim().length > 0;
  /* Continue needs both answers. The manual-setup link needs only the name — picking a
   * type is precisely what the reader taking it could not do. */
  const valid = named && kind !== null;

  // The derived agent_id must be unique in the workspace (the backend 409s), so catch it
  // client-side before creating anything. `clash` is the row to adopt, if any.
  const clash =
    editing || !named
      ? undefined
      : (existingAgents ?? []).find((a) => a.agent_id === agentId);
  const duplicate = clash !== undefined;
  const duplicateMessage = `An agent named “${name.trim()}” already exists. Continue with it, or pick a different name.`;

  /* The CLI signs the reader in and creates its own key, so a key minted here for an agent
   * it will configure would go unused and never expire. If they turn to the snippet
   * instead, the wizard mints one then. */
  const mintKeyUnlessCli = async (
    forKind: AgentKind,
    forAgentId: string,
    keyName: string,
  ) => {
    if (cliRouteOffered(forKind, forAgentId)) return;

    const key = await identity.createApiKey(workspace.id, `${keyName} API key`);
    trackApiKeyCreated(key.workspaceId);
    setAi21Key(key.value);
  };

  /* `kindOverride` carries the kind the picker is not holding: the hand-configured one
   * for the manual-setup link (it has no tile to select), and the clashing agent's own
   * kind when adopting it. Whatever the reader sees in the name field is what gets
   * created — the field is editable and in front of them, so nothing renames it here. */
  const submit = async (
    adopt = false,
    kindOverride?: AgentKind,
    trackAs: string = ctaLabel,
  ) => {
    const chosenKind = kindOverride ?? kind;
    if (!named || busy || chosenKind === null || (duplicate && !adopt)) return;

    trackButtonPressed(trackAs);
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await agents.update(agentId, { kind: chosenKind, name: name.trim() });
      } else if (clash) {
        /* Taken as it is: writing the typed name and kind over it would silently rename or
         * retype a live agent that merely shares the slug. Only the key is new. */
        await mintKeyUnlessCli(clash.kind, clash.agent_id, clash.name);
        invalidateQueries(`${workspaceKeyPrefix()}agents`);
        bumpRefresh();
        selectNewAgent(clash.agent_id);
        onContinue({
          agentId: clash.agent_id,
          kind: clash.kind,
          name: clash.name,
        });

        return;
      } else {
        // Create the agent first: if the id collides (409) we bail before
        // minting a key, so a failed attempt never leaves an orphaned key.
        await agents.create({
          agent_id: agentId,
          kind: chosenKind,
          name: name.trim(),
          enabled: true,
        });
        await mintKeyUnlessCli(chosenKind, agentId, name.trim());
      }

      // Drop the workspace-scoped agents cache so WorkspaceHome refetches; its stale empty
      // list bounces the user back to /welcome. The key is workspace-prefixed (useAgents).
      invalidateQueries(`${workspaceKeyPrefix()}agents`);
      // The dashboard's agent list is an analytics rollup (useAgentRollups), which a cache
      // clear won't refetch; bumpRefresh re-keys mounted analytics hooks so it shows now.
      bumpRefresh();
      selectNewAgent(agentId);
      onContinue({ agentId, kind: chosenKind, name: name.trim() });
    } catch (err) {
      // Otherwise only shown inline; log it so a broken signup/setup is visible in Loki.
      logger.error(
        err instanceof Error ? err : new Error(friendlyError(err).message),
        "onboarding agent setup failed",
        {
          origin:
            editing || adopt
              ? "onboardingUpdateAgent"
              : "onboardingCreateAgent",
        },
      );
      setError(friendlyError(err).message);
      setBusy(false);
    }
  };

  return (
    <WizardFrame
      step={0}
      lead="Give your agent a name and tell us what you're connecting."
    >
      {/* Per the Figma guideline the fields sit directly on the step, no bordered card. */}
      <div className="flex flex-col gap-5">
        {/* The name stays editable; only the agent_id derived from it is frozen once the
          agent exists, since every snippet and printed command already refers to it. */}
        <TextField
          label={<FieldLabel>Agent name</FieldLabel>}
          placeholder="My agent"
          value={name}
          autoFocus
          disabled={busy}
          onChange={(e) => {
            setTypedName(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter")
              void submit(
                false,
                undefined,
                editing ? "Save and continue" : ctaLabel,
              );
          }}
          error={error ?? (duplicate ? duplicateMessage : undefined)}
        />

        <div className="flex flex-col gap-1.5">
          <FieldLabel>Agent type</FieldLabel>
          <AgentTypePicker value={kind} onChange={setKind} disabled={busy} />
        </div>
      </div>

      <ProviderKeyNote kind={kind} />

      {/* The way out of a dead end: the name is almost certainly an agent just abandoned. */}
      {duplicate && (
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            void submit(true, clash?.kind, "Continue with the existing agent")
          }
          className="self-start t-caption transition-colors"
          style={{ color: "var(--onb-link)", fontWeight: 600 }}
        >
          Continue with the existing “{name.trim()}”
        </button>
      )}

      <WizardCta
        className="w-full"
        disabled={!valid || busy || duplicate}
        onClick={() =>
          void submit(
            false,
            undefined,
            editing ? "Save and continue" : ctaLabel,
          )
        }
        iconRight={busy ? undefined : <ArrowRight />}
      >
        {busy
          ? editing
            ? "Updating…"
            : "Setting up…"
          : editing
            ? "Save and continue"
            : ctaLabel}
      </WizardCta>

      {/* The way to the hand-configuration guide now that it has no tile: below the CTA,
        in caption grey, so it reads as the fallback it is rather than a fourth option. */}
      <button
        type="button"
        disabled={!named || busy || duplicate}
        onClick={() => void submit(false, MANUAL_AGENT_KIND, MANUAL_SETUP_LINK)}
        className="self-start t-caption transition-colors disabled:cursor-not-allowed disabled:opacity-60"
        style={{ color: "var(--fg-3)" }}
      >
        Didn’t find your client type?{" "}
        {/* --accent-deep, not --onb-link: the pink link is drawn for the drawer's maroon
          ground and only reaches 2.5:1 on the light card. --accent-deep is carmine here
          and pink on the maroon ground (app-theme.css), so both pass AA. */}
        <span style={{ color: "var(--accent-deep)", fontWeight: 500 }}>
          {MANUAL_SETUP_LINK}
        </span>
      </button>
    </WizardFrame>
  );
}

/* The 401 guard: readers were configuring the AI21 key instead of their own provider
 * credential, not alongside it, and their first request came back 401.
 *
 * A hairline block, not the crimson tint the tiles use: in the Gateway Design System a
 * tint marks the summary layer and a hairline marks the detail layer, and the accent fill
 * belongs to the one primary action on screen — which here is Continue. The weight comes
 * from the glyph and the headline instead, which is also how that system carries status.
 *
 * The headline stands alone until a tile is picked, since the picker opens empty and this
 * is true of every kind; the per-kind line then names that kind's own credential. */
function ProviderKeyNote({ kind }: { kind: AgentKind | null }) {
  return (
    <div
      className="flex items-start gap-2.5 rounded-[5px] border p-3"
      style={{
        background: "var(--surface-2)",
        borderColor: "var(--onb-track)",
      }}
    >
      <Key
        size={16}
        aria-hidden
        className="mt-px shrink-0"
        style={{ color: "var(--fg-3)" }}
      />
      <div className="flex flex-col gap-1">
        <p
          className="text-[13px]"
          style={{ color: "var(--fg-1)", fontWeight: 600 }}
        >
          {PROVIDER_KEY_HEADLINE}
        </p>
        {kind && (
          <p className="text-[12px] text-[var(--text-muted)]">
            {PROVIDER_KEY_COPY[kind]}
          </p>
        )}
      </div>
    </div>
  );
}
